import React from "react";
import { intl } from "LocaleWrapper";
import { useApolloClient } from "@apollo/client";

import { Table, Filter, FilterValues, PageHeader, Card } from "@cofano/cck";

import { TableId, useBackendTable } from "components/table";
import LocationSelect, { LocationSelectOption } from "view/locations/components/selects/LocationSelect";
import ShipSelect, { ShipSelectOption } from "view/ships/components/selects/ShipSelect";
import Helmet from "components/helmet/Helmet";

import { Language } from "imports/Utils";

import { LocationLink } from "utils/LinkUtils";
import { FormattedDate } from "utils/DateUtils";

import { GetLoggedGeofencesDocument, GetLoggedGeofencesQuery } from "__generated__/view/geofences/graphql/queries.generated";
import { ChildPropsFromPagination } from "types/commons";

type LoggedGeofenceFromQuery = ChildPropsFromPagination<GetLoggedGeofencesQuery, "loggedGeofences">

const GeofenceLogs = () => {
	const client = useApolloClient();

	const { tableProps, filterProps, filterValuesProps } = useBackendTable({
		id: TableId.GeofenceLogs,
		onChange: async(pagination, sort, filter, callback) => {
			try {
				const result = await client.query({
					query: GetLoggedGeofencesDocument,
					variables: {
						pagination: {
							...pagination,
							sortList: sort,
						},
						search: {
							ships: filter.ship?.map((o: ShipSelectOption) => o.id),
							locations: filter.location?.map((o: LocationSelectOption) => o.id),
						},
					},
				});

				callback({
					content: result.data.loggedGeofences?.content ?? [],
					totalResults: result.data.loggedGeofences?.totalElements ?? 0,
				});
			} catch {
				callback({
					content: [],
					totalResults: 0,
				});
			}
		},
	});

	return (
		<React.Fragment>
			<PageHeader>
				<PageHeader.Title>
					{Language("SHIP_LOG")}
					<Helmet title={intl.formatMessage({ id: "SHIP_LOG" })} />
				</PageHeader.Title>
			</PageHeader>

			{filterValuesProps.values.length > 0 && (
				<div style={{ marginBottom: 16 }}>
					<Card disablePadding>
						<FilterValues
							{...filterValuesProps}
							fields={[ {
								id: "ship",
								title: intl.formatMessage({ id: "SHIPS" }),
								renderFilterValue: (value) => value.map((option: ShipSelectOption) => option.name).join(", "),
							}, {
								id: "location",
								title: intl.formatMessage({ id: "LOCATIONS" }),
								renderFilterValue: (value) => value.map((option: LocationSelectOption) => option.description).join(", "),
							} ]} />
					</Card>
				</div>
			)}

			<Card disablePadding>
				<Table
					{...tableProps}
					filterComponent={() => (
						<Filter
							{...filterProps}
							fields={[ {
								id: "ship",
								title: intl.formatMessage({ id: "SHIPS" }),
								render: (value, onChange) => (
									<ShipSelect
										value={value}
										onChange={options => onChange(options)}
										multiple />
								),
							}, {
								id: "location",
								title: intl.formatMessage({ id: "LOCATIONS" }),
								render: (value, onChange) => (
									<LocationSelect
										value={value}
										onChange={options => onChange(options)}
										multiple />
								),
							} ]} />
					)}
					columns={[ {
						id: "ship",
						title: intl.formatMessage({ id: "SHIPS_SINGLE" }),
						render: (row: LoggedGeofenceFromQuery) => row.ship?.name,
						width: 200,
						filterComponent: (value, onChange) => (
							<ShipSelect
								label={intl.formatMessage({ id: "SHIPS" })}
								value={value}
								onChange={options => onChange(options)}
								multiple />
						),
						sortable: true,
					}, {
						id: "location",
						title: intl.formatMessage({ id: "LOCATION" }),
						render: (row: LoggedGeofenceFromQuery) => <LocationLink location={row.location} />,
						width: 200,
						filterComponent: (value, onChange) => (
							<LocationSelect
								label={intl.formatMessage({ id: "LOCATIONS" })}
								value={value}
								onChange={options => onChange(options)}
								multiple />
						),
						sortable: true,
					}, {
						id: "enteredDateTime",
						title: intl.formatMessage({ id: "SHIP_LOG_ENTERED_DATETIME" }),
						render: (row: LoggedGeofenceFromQuery) => <FormattedDate date={row.enteredDateTime} />,
						sortable: true,
					}, {
						id: "exitedDateTime",
						title: intl.formatMessage({ id: "SHIP_LOG_EXITED_DATETIME" }),
						render: (row: LoggedGeofenceFromQuery) => <FormattedDate date={row.exitedDateTime} />,
						sortable: true,
					} ]} />
			</Card>
		</React.Fragment>
	);
};

export default GeofenceLogs;
